import { StreamProcessorSrc } from "./stream_processor";

export default class WavStreamPlayer {
  private readonly scriptSrc: string;
  private readonly sampleRate: number;
  context: AudioContext | null;
  stream: AudioWorkletNode | null;
  private readonly trackSampleOffsets: Record<
    string,
    {
      trackId: string;
      offset: number;
      currentTime: number;
    }
  >;
  private readonly interruptedTrackIds: Record<string, boolean>;

  constructor({ sampleRate } = { sampleRate: 24000 }) {
    this.scriptSrc = StreamProcessorSrc;
    this.sampleRate = sampleRate;
    this.context = null;
    this.stream = null;
    this.trackSampleOffsets = {};
    this.interruptedTrackIds = {};
  }

  async connect() {
    this.context = new AudioContext({ sampleRate: this.sampleRate });
    if (this.context.state === "suspended") {
      await this.context.resume();
    }
    try {
      await this.context.audioWorklet.addModule(this.scriptSrc);
    } catch (e) {
      console.error(e);
      throw new Error(`Could not add audioWorklet module: ${this.scriptSrc}`);
    }
    return true;
  }

  private _start() {
    if (!this.context) throw new Error("AudioContext not connected");
    const streamNode = new AudioWorkletNode(this.context, "stream_processor");
    streamNode.connect(this.context.destination);

    streamNode.port.onmessage = (
      e: MessageEvent<{
        event: string;
        requestId: string;
        trackId: string;
        offset: number;
      }>,
    ) => {
      const { event } = e.data;
      switch (event) {
        case "stop":
          streamNode.disconnect();
          this.stream = null;
          break;
        case "offset": {
          const { requestId, trackId, offset } = e.data;
          const currentTime = offset / this.sampleRate;
          this.trackSampleOffsets[requestId] = { trackId, offset, currentTime };
          break;
        }
        case "completed": {
          const event = new CustomEvent("audio_completed");
          window.dispatchEvent(event);
          streamNode.disconnect();
          this.stream = null;
          break;
        }
      }
    };
    this.stream = streamNode;
    return true;
  }

  add16BitPCM(arrayBuffer: Int16Array | ArrayBuffer, trackId = "default") {
    if (this.interruptedTrackIds[trackId]) {
      return;
    }
    if (!this.stream) {
      this._start();
    }
    let buffer;
    if (arrayBuffer instanceof Int16Array) {
      buffer = arrayBuffer;
    } else {
      buffer = new Int16Array(arrayBuffer);
    }

    this.stream?.port.postMessage({ event: "write", buffer, trackId });
    return buffer;
  }

  async getTrackSampleOffset(interrupt = false) {
    if (!this.stream) {
      return null;
    }
    const requestId = crypto.randomUUID();
    this.stream.port.postMessage({
      event: interrupt ? "interrupt" : "offset",
      requestId,
    });
    let trackSampleOffset;
    while (!trackSampleOffset) {
      trackSampleOffset = this.trackSampleOffsets[requestId];
      await new Promise((r) => setTimeout(() => r(null), 1));
    }
    const { trackId } = trackSampleOffset;
    if (interrupt && trackId) {
      this.interruptedTrackIds[trackId] = true;
    }
    return trackSampleOffset;
  }

  async interrupt() {
    return this.getTrackSampleOffset(true);
  }

  close() {
    this.context?.close();
  }
}
